// Canonical English dictionary — the source of truth for every key. nl.js,
// de.js and fr.js must mirror this exact shape; missing keys fall back to
// this file (see LanguageContext.js), so an incomplete translation degrades
// to English rather than breaking.
const en = {
  common: {
    languages: { en: "English", de: "Deutsch", nl: "Nederlands", fr: "Français" },
  },

  maintenance: {
    title: "Under Maintenance",
    body: "Sibolab is currently undergoing maintenance. We'll be back online shortly. Thank you for your patience.",
  },

  updateBanner: {
    message: "A new version of the portal is available.",
    refresh: "Refresh",
    close: "Close",
  },

  session: {
    accessChanged: "Your access rights have changed, so some pages are no longer available to you. Please contact Sibolab if you think this is a mistake.",
  },

  sidebar: {
    portalLabels: {
      admin: "Open Admin Portal",
      therapist: "Open Therapist Portal",
      patient: "Open Customer Portal",
    },
    roleLabels: {
      admin: "Admin",
      operator: "Operator",
      therapist: "Therapist",
      patient: "Customer",
    },
    nav: {
      dashboard: "Dashboard",
      myOrders: "My Orders",
      myReports: "My Reports",
      myPatients: "My Patients",
      reports: "Reports",
    },
    account: {
      signedIn: "Signed in",
      settings: "Settings",
      logout: "Log out",
    },
  },

  languageSwitcher: {
    promptTitle: "Change portal language?",
    promptBody: "Should this be your language every time you sign in, or just for this session?",
    justForNow: "Just for now",
    saveForNextTime: "Save for next time",
  },

  twoFactorPrompt: {
    title: "How would you like to log in?",
    body: "For extra security, we can email you a one-time code every time you log in. If you turn this off, you'll be logged in with just your email and password.",
    enable: "Keep email code (recommended)",
    disable: "Log in without code",
    changeLater: "You can change this anytime in Settings → Account.",
    error: "Couldn't save your choice. Please try again.",
  },
  settings: {
    dialog: {
      groupLabel: "Settings",
      general: "General",
      account: "Account",
    },
    unsaved: {
      title: "Unsaved changes",
      body: "You have changes that haven't been saved yet. Do you want to save them before leaving?",
      save: "Save changes",
      discard: "Discard",
      keepEditing: "Keep editing",
      saveError: "Some changes couldn't be saved. Please check the messages on the page.",
    },
    general: {
      title: "Profile",
      fullName: "Full name",
      country: "Country",
      countryPlaceholder: "Select a country",
      language: "Language",
      languagePlaceholder: "Select a language",
      lastLogin: "Last login: {{date}}",
      memberSince: "Member since: {{date}}",
      saveChanges: "Save changes",
      profileUpdated: "Profile updated",
      loadError: "Failed to load profile",
      saveError: "Failed to update profile",
    },
    account: {
      title: "Account",
      changePassword: "Change password",
      previousPasswordPlaceholder: "Previous password",
      newPasswordPlaceholder: "New password",
      confirmNewPasswordPlaceholder: "Confirm new password",
      cancel: "Cancel",
      updatePassword: "Update password",
      passwordUpdated: "Password updated successfully.",
      errors: {
        fillAllFields: "Please fill in all password fields.",
        mismatch: "New password and confirmation do not match.",
        requirements: "New password does not meet the requirements above.",
        generic: "Failed to change password",
      },
      twoFactor: {
        title: "Two-step login (email code)",
        label: "Ask for an email code when I log in",
        noteOn: "We'll email you a one-time code each time you log in.",
        noteOff: "You'll log in with just your email and password (less secure).",
        save: "Save",
        saved: "Two-step login setting saved.",
        saveError: "Failed to save the two-step login setting.",
        loadError: "Couldn't load your two-step login setting.",
        confirmTitleOn: "Turn on two-step login?",
        confirmBodyOn: "From your next login on, we'll email you a one-time code every time you log in.",
        confirmTitleOff: "Turn off two-step login?",
        confirmBodyOff: "From your next login on, you'll be logged in with just your email and password - no email code. This is less secure.",
        confirm: "Confirm",
        cancel: "Cancel",
      },
      communicationPreferences: {
        title: "Communication preferences",
        loadError: "Couldn't load your communication preferences.",
        savePreferences: "Save preferences",
        saved: "Preferences saved.",
        saveError: "Failed to save preferences.",
      },
      therapistApplication: {
        title: "Therapist application",
        pending: "Your application is under review.",
        revoked: "Your therapist access has been revoked. Contact support if you have questions.",
        rejected: "Your application wasn't approved. Contact support if you have questions.",
        reapply: "Re-apply",
        interested: "Interested in offering your services through Sibolab? Apply to become a therapist.",
        apply: "Apply to become a therapist",
        cancel: "Cancel",
        submit: "Submit application",
        errors: {
          fieldsRequired: "Fill in your practice name, city and email.",
          docsRequired: "Add at least one license or certificate document.",
          generic: "Something went wrong submitting your application.",
        },
      },
    },
  },

  auth: {
    emailHint: "The email can take a minute to arrive. Please also check your spam or junk folder.",
    layout: {
      title: "Portal Sibolab",
      subtitleDesktop:
        "Manage lab tests, track orders, analyze results, and streamline your laboratory workflow — all in one place.",
      subtitleMobile: "Manage lab tests, track orders, and view results in one place.",
      copyright: "© {{year}} Sibolab. All rights reserved.",
    },

    login: {
      title: "Sign in",
      subtitle: "Enter your credentials to access the portal",
      emailPlaceholder: "your@email.com",
      passwordPlaceholder: "Password",
      emailRequired: "Email is required",
      invalidEmail: "Invalid email",
      passwordRequired: "Password is required",
      forgotPassword: "Forgot password?",
      submit: "Sign in",
      submitting: "Signing in...",
      newToSibolab: "New to Sibolab?",
      createAccount: "Create an account",
      errors: {
        shadowAccount: "You don't have a portal account yet. Please create one with this email address.",
        badRequest: "Email and password are required.",
        invalidCredentials: "Incorrect email or password.",
        inactive: "This account is inactive. Contact an administrator.",
        tooMany: "Too many login attempts. Please wait a few minutes and try again.",
        generic: "Unable to sign in right now. Check your connection and try again.",
      },
      otp: {
        title: "Enter verification code",
        subtitle: "We sent a 6-digit code to {{email}}. Enter it below.",
        incorrectCode: "Incorrect or expired code. Please check the code in your email and try again, or request a new one.",
        verify: "Verify code",
        verifying: "Verifying…",
        notSeeing: "Not seeing the email?",
        sendNewCode: "Send a new code",
        sending: "Sending…",
        wrongEmail: "Wrong email?",
        changeEmail: "Change email address",
        resendNotice: "A new code was sent - check your email and enter it below.",
        resendError: "Couldn't resend. Please try again.",
      },
    },

    register: {
      emailStep: {
        title: "Create your account",
        subtitle: "Enter your email to get started.",
        submit: "Continue",
      },
      alreadyHaveAccount: "Already have an account?",
      signIn: "Sign in",
      errors: {
        tooMany: "Too many attempts - please wait a few minutes and try again.",
        generic: "Something went wrong. Please try again.",
      },
      accountExists: {
        title: "Account already exists",
        withTherapist:
          "Account already exists. Log in, and you can apply to become a therapist from your account if you'd like.",
        withoutTherapist: "Account already exists, please log in.",
        goToLogin: "Go to login",
        wrongEmail: "Wrong email?",
        tryDifferent: "Try a different email",
      },
      otp: {
        title: "Check your email",
        subtitle: "We sent a 6-digit code to {{email}}. Enter it below.",
        tooMany: "Too many attempts, please try again later.",
        incorrectCode: "Incorrect or expired code. Please check the code in your email and try again, or request a new one.",
        verify: "Verify code",
        checking: "Checking…",
        resent: "Code resent - check your email.",
        notSeeing: "Not seeing the email?",
        sendNewCode: "Send a new code",
        wrongEmail: "Wrong email?",
        changeEmail: "Change email address",
        resendError: "Couldn't resend the code. Please try again.",
      },
      completeErrors: {
        expiredCode: "Your code has expired. Please verify again.",
        accountExists: "An account with this email already exists.",
        tooMany: "Too many attempts, please try again later.",
        generic: "Something went wrong. Please try again.",
      },
      therapistApplyFailure:
        "Account created - your therapist application couldn't be submitted: {{reason}}. You can retry from Account Settings.",
    },

    registerDetails: {
      title: "Finish creating your account",
      subtitle: "Code verified - a few more details and you're in.",
      namePlaceholder: "Name (optional)",
      countryPlaceholder: "Select a country",
      languagePlaceholder: "Select a language",
      countryRequired: "Please select your country",
      languageRequired: "Please select your language",
      passwordPlaceholder: "Password",
      confirmPasswordPlaceholder: "Confirm password",
      passwordRequired: "Password is required",
      passwordRequirements: "Password does not meet the requirements above",
      passwordsMismatch: "Passwords do not match",
      therapistFieldsRequired: "Fill in your practice name, city and email to apply as a therapist",
      therapistDocsRequired: "Add at least one license or certificate document",
      registerAsTherapist: "Register as a therapist",
      registerAsTherapistDesc: "Submit your practice details and license for admin review.",
      submit: "Create account",
      submitting: "Please wait…",
    },

    forgotPassword: {
      title: "Reset your password",
      subtitle: "Enter the email on your account and we'll send you a code.",
      submit: "Send reset code",
      rememberedIt: "Remembered it?",
      signIn: "Sign in",
      errors: {
        tooMany: "Too many attempts — please wait a few minutes and try again.",
        generic: "Something went wrong. Please try again.",
      },
      code: {
        title: "Enter the verification code",
        subtitle: "If an account exists for {{email}}, we sent a 6-digit code. Enter it below.",
        verify: "Verify code",
        checking: "Checking…",
        resent: "Code resent — check your email.",
        notSeeing: "Not seeing the email?",
        tryAgain: "Try sending again",
        wrongEmail: "Wrong email?",
        changeEmail: "Change email address",
        incorrectCode: "Incorrect or expired code. Please check the code in your email and try again, or request a new one.",
        tooMany: "Too many attempts, please try again later.",
        resendError: "Couldn't resend the code. Please try again.",
      },
      done: {
        title: "Password updated",
        subtitle: "You can now log in with your new password.",
        goToSignIn: "Go to sign in",
      },
      completeErrors: {
        tooMany: "Too many attempts, please try again later.",
        generic: "Something went wrong. Please try again.",
      },
      needsRegistration: {
        title: "No password set yet",
        subtitle:
          "We found an account for this email, but no password has been set yet. Create one to get access.",
        cta: "Create your account",
      },
      notFound: {
        title: "No account found",
        subtitle: "We couldn't find an account for this email address.",
        cta: "Create an account",
        tryDifferentEmail: "Try a different email",
      },
    },

    expiredLink: {
      title: "This code has expired",
      contactPrefix: "If the problem persists, please",
      contactLink: "contact support",
      tryAgain: "Try again",
    },

    setNewPassword: {
      title: "Set a new password",
      subtitle: "Code confirmed — choose a new password for your account.",
      newPasswordPlaceholder: "New password",
      confirmPasswordPlaceholder: "Confirm new password",
      passwordRequired: "Password is required",
      passwordRequirements: "Password does not meet the requirements above",
      passwordsMismatch: "Passwords do not match",
      submit: "Update password",
      submitting: "Please wait…",
    },

    emailStep: {
      emailRequired: "Email is required",
      invalidEmail: "Invalid email",
    },

    passwordStrength: {
      length: "At least 8 characters",
      lowercase: "One lowercase letter",
      uppercase: "One uppercase letter",
      number: "One number",
      special: "One special character",
    },

    consent: {
      portalUpdates: "Keep me updated about new portal features and announcements.",
      marketing: "Send me the IBS/SIBO/IMO newsletter.",
    },

    therapistApply: {
      practiceDetails: "Practice details",
      reviewNotice: "An admin reviews this before your therapist account is activated.",
      practiceName: "Practice name",
      practiceNamePlaceholderPortal: "e.g. Kunz Naturheilpraxis",
      practiceCity: "Practice city",
      practiceCityPlaceholderPortal: "e.g. Berlin",
      practiceEmail: "Practice email",
      practiceEmailHelp: "This is your account email and can't be changed here.",
      licenseCertificates: "License / certificates",
    },

    documentUpload: {
      clickOrDrag: "Click to upload or drag and drop",
      hint: "PDF, PNG or JPEG, up to 10MB each - {{count}}/{{max}} added.",
      tooMany: "You can upload up to {{max}} documents.",
      invalidType: "{{name}}: only PDF, PNG and JPEG files are allowed.",
      tooLarge: "{{name}}: file exceeds the 10MB limit.",
    },

    copyButton: {
      copy: "Copy code",
      copied: "Copied",
    },
  },

  portal: {
    statusTimeline: {
      steps: {
        unprocessed: {
          label: "Order received",
          bullets: ["Your order has been received and is being prepared."],
        },
        dispatched: {
          label: "Kit dispatched",
          bullets: [
            "Your test kit is on its way to you.",
            "You'll receive tracking information via email.",
          ],
        },
        received_by_customer: {
          label: "Kit delivered",
          bullets: [
            "Your test kit has been delivered.",
            "Please complete the test and return your sample using the prepaid label.",
          ],
        },
        returned_to_lab: {
          label: "Sample received by lab",
          bullets: [
            "We've received your returned sample.",
            "It's now queued for analysis.",
          ],
        },
        processing: {
          label: "Analysis in progress",
          bullets: [
            "Your sample is being analyzed.",
            "Your report will be ready in 2-3 business days.",
          ],
        },
        analysis_completed: {
          label: "Analysis complete",
          bullets: [
            "Your analysis is complete.",
            "Your report will be delivered soon.",
          ],
        },
        completed: {
          label: "Report ready",
          bullets: ["Your report is ready.", "You can view or download it below."],
        },
        cancelled: {
          label: "Cancelled",
          bullets: ["This test has been cancelled."],
        },
      },
      cancelledReasons: {
        customer: "Cancelled by customer",
      },
      tooltip: {
        completed: "{{label}} — Completed {{date}}",
        inProgress: "{{label}} — In progress since {{date}}",
      },
    },

    statusFilterChips: {
      all: "All",
    },

    orderTestActionChips: {
      copied: "Copied",
      copyFormLink: "Copy form link",
      fillOutForm: "Fill out your form",
      reportProcessing: "Report processing",
    },

    viewDownloadButtons: {
      viewReport: "View Report",
      download: "Download",
      reportNotFound: "Report not found.",
      fetchLinkError: "Couldn't fetch the report link.",
    },

    roleSwitchIndicator: {
      viewingAs: "Viewing as {{role}}",
    },

    dashboard: {
      recentActivity: {
        title: "Recent Activity",
        subtitle: "Your last 5 status changes",
        empty: "No recent activity yet.",
      },
      patient: {
        pageTitle: "Dashboard",
        pageSubtitle: "An overview of your orders and reports.",
        summary: {
          totalOrders: "Total Orders",
          inProgress: "In Progress",
          completed: "Completed",
          reportsAvailable: "Reports Available",
        },
        reportsReady: {
          one: "{{count}} report ready to view",
          other: "{{count}} reports ready to view",
        },
        goToReports: "Go to My Reports",
        loadError: "Failed to load your dashboard.",
      },
      therapist: {
        pageTitle: "Dashboard",
        pageSubtitle: "An overview of your patients and tests.",
        summary: {
          totalPatients: "Total Patients",
          totalTests: "Total Tests",
          completedThisMonth: "Completed This Month",
          inProgress: "In Progress",
        },
        loadError: "Failed to load your dashboard.",
      },
    },

    orders: {
      patient: {
        title: "My Orders",
        subtitle: "Track your lab test kits from order to report.",
        emptyAll: "You don't have any orders yet.",
        emptyFiltered: "No orders match this filter.",
        loadError: "Failed to load your orders.",
      },
      therapist: {
        title: "Patient Orders",
        subtitle: "Test progress for this patient.",
        backLabel: "Back to patients",
        emptyAll: "No tests for this patient yet.",
        emptyFiltered: "No tests match this filter.",
        loadError: "Failed to load orders.",
        invalidPatient: "This patient link is invalid.",
      },
    },

    reports: {
      table: {
        order: "Order",
        test: "Test",
        substrate: "Substrate",
        completed: "Completed",
        report: "Report",
      },
      empty: "No reports yet.",
      emptyFiltered: "No reports match these filters.",
      filters: {
        searchPlaceholder: "Search by order, test ID, or substrate",
        dateAny: "All dates",
        clear: "Clear",
        done: "Done",
      },
      patient: {
        title: "My Reports",
        subtitle: "Lab test reports linked to your account.",
        loadError: "Failed to load your reports.",
      },
      therapist: {
        title: "Reports",
        subtitle: "Reports across every patient linked to you.",
        loadError: "Failed to load reports.",
      },
    },

    patients: {
      title: "My Patients",
      subtitle: "Patients whose tests route to you.",
      searchPlaceholder: "Search by patient name",
      emptySearch: "No patients match your search.",
      emptyAll: "No patients yet.",
      loadError: "Failed to load your patients.",
      table: {
        patient: "Patient",
        dateOfBirth: "Date of birth",
        tests: "Tests",
        latestTest: "Latest test",
      },
    },

    noAccess: {
      title: "No portal access on this account",
      subtitle:
        "Your account isn't currently assigned to any portal. If you believe this is a mistake, contact support at info@sibolab.de.",
    },
  },
};

export default en;
